// One fetch, one schema, one failure mode.
//
// The manifest is served per space (not per page) and cached like any other
// runtime asset, so reading it is a plain GET the browser can satisfy from
// cache. Anything that is not a v5 document is an error with the version in it:
// a stale runtime serving v4 is the one failure worth naming, because the SDK
// and the engine deploy independently.

import { z } from "zod";

import {
  RUNTIME_COLLAB_MANIFEST_PATH,
  RUNTIME_COLLAB_THEME_PATH,
} from "@spacefast/common/utils/runtime-paths";

import { collabManifestSchema, type CollabManifest } from "./manifest.js";

/** Where the runtime serves it — the reserved namespace, composed in
 *  `@spacefast/common` so this spelling and the PHP one cannot drift.
 *  Absolute paths resolve against the page. */
export const COLLAB_MANIFEST_PATH: string = RUNTIME_COLLAB_MANIFEST_PATH;

/** The space's theme tokens, beside the manifest. */
export const COLLAB_THEME_PATH: string = RUNTIME_COLLAB_THEME_PATH;

/**
 * The theme stylesheet that goes with a manifest URL: same query, so a preview
 * session's token rides both and they resolve against one serving state.
 */
export function collabThemeUrl(manifestUrl: string = COLLAB_MANIFEST_PATH): string {
  const query = manifestUrl.split("?", 2)[1];
  return query ? `${COLLAB_THEME_PATH}?${query}` : COLLAB_THEME_PATH;
}

/** Just enough of the body to say what it claims to be, for a legible error. */
const claimedVersionSchema = z.object({ version: z.union([z.number(), z.string()]) });

export async function fetchCollabManifest(
  url: string = COLLAB_MANIFEST_PATH,
): Promise<CollabManifest> {
  const target = new URL(url, globalThis.location?.href ?? "http://localhost/");
  const response = await fetch(target, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`collab manifest request failed (${response.status})`);
  }
  const body: unknown = await response.json();
  const parsed = collabManifestSchema.safeParse(body);
  if (!parsed.success) {
    const claimed = claimedVersionSchema.safeParse(body);
    const found = claimed.success
      ? `version ${JSON.stringify(claimed.data.version)}`
      : "no version";
    throw new Error(`collab manifest at ${target.pathname} is not version 5 (found ${found})`);
  }
  return parsed.data;
}
